// src/components/SimpleComponents/SimpleEntityForm/pt/SimpleAddressFormPT.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React from 'react';
import { Col, Row } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import SimpleInput from '../../SimpleInput';
import { useSimpleFormContext } from '../../SimpleFormProvider';
import useFormElement from 'hooks/useFormElement';
import SimpleAddressZip from '../../SimpleAddressZip';
import SimpleGoogleMap from '../../SimpleGoogleMap';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Portugal address labels (DE/PARA from BR):
//   zip          -> postal_code  (Codigo Postal)
//   address1     -> morada       (Morada / Logradouro)
//   number       -> number       (Numero)
//   address2     -> fraction     (Fracao / Complemento)
//   neighborhood -> locality     (Localidade / Bairro)
//   city/__city  -> municipality (Concelho / Cidade)
//   region       -> district     (Distrito / Estado)
// Field names and DB schema are IDENTICAL to BR; only labels differ.

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleAddressFormPT = ({
  subdocumentField,
  showNeighborhood = false,
  showAddressReference = false,
  showMap = false,
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const {
    handleAddressZipBlur,
    handleAddressBlur,
    handleAddressNumberBlur,
    handleAddressChange,
    handleAddressNumberChange,
    getFormZipState,
  } = useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const zipElementProps = useFormElement('zip', subdocumentField);
  const address1ElementProps = useFormElement('address1', subdocumentField);
  const address2ElementProps = useFormElement('address2', subdocumentField);
  const numberElementProps = useFormElement('number', subdocumentField);
  const neighborhoodElementProps = useFormElement('neighborhood', subdocumentField);
  const cityElementProps = useFormElement('city', subdocumentField);
  const regionElementProps = useFormElement('region', subdocumentField);
  const addressReferenceElementProps = useFormElement('addressReference', subdocumentField);

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const zipState = getFormZipState(zipElementProps.flattenFieldName);

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleAddressZipBlurWrapper = ev => {
    handleAddressZipBlur?.({
      zip: zipElementProps,
      address: address1ElementProps,
      number: numberElementProps,

      ev,
      value: ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '',
      subdocumentField,
      fullParentFieldName: zipElementProps.fullParentFieldName,
      flattenParentFieldName: zipElementProps.flattenParentFieldName,
    });
  };

  const handleAddressNumberBlurWrapper = ev => {
    handleAddressNumberBlur?.({
      zip: zipElementProps,
      address: address1ElementProps,
      number: numberElementProps,

      ev,
      value: ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '',
      subdocumentField,
      fullParentFieldName: zipElementProps.fullParentFieldName,
      flattenParentFieldName: zipElementProps.flattenParentFieldName,
    });
  };

  const handleAddressNumberChangeWrapper = ev => {
    handleAddressNumberChange?.({
      zip: zipElementProps,
      address: address1ElementProps,
      number: numberElementProps,

      ev,
      value: ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '',
      subdocumentField,
      fullParentFieldName: zipElementProps.fullParentFieldName,
      flattenParentFieldName: zipElementProps.flattenParentFieldName,
    });
  };

  const handleAddressChangeWrapper = ev => {
    handleAddressChange?.({
      zip: zipElementProps,
      address: address1ElementProps,
      number: numberElementProps,
      city: cityElementProps,
      region: regionElementProps,

      ev,
      value: ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '',
      subdocumentField,
      fullParentFieldName: zipElementProps.fullParentFieldName,
      flattenParentFieldName: zipElementProps.flattenParentFieldName,
    });
  };

  const handleAddressBlurWrapper = ev => {
    handleAddressBlur?.({
      zip: zipElementProps,
      address: address1ElementProps,
      number: numberElementProps,
      city: cityElementProps,
      region: regionElementProps,

      ev,
      value: ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '',
      subdocumentField,
      fullParentFieldName: zipElementProps.fullParentFieldName,
      flattenParentFieldName: zipElementProps.flattenParentFieldName,
    });
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <>
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={6}>
          <SimpleAddressZip
            countryCode="PT"
            name={zipElementProps.fullFieldName}
            label="postal_code"
            onBlur={handleAddressZipBlurWrapper}
            loading={zipState?.searching}
          />
        </Col>

        <Col xs={24} sm={13}>
          <SimpleInput
            name={address1ElementProps.fullFieldName}
            label="morada"
            disabled={!zipState?.zipWithMultipleAddress && !zipState?.apiFailure}
            required={zipState?.zipWithMultipleAddress || zipState?.apiFailure}
            onChange={handleAddressChangeWrapper}
            onBlur={handleAddressBlurWrapper}
          />
        </Col>

        <Col xs={24} sm={5}>
          <SimpleInput
            name={numberElementProps.fullFieldName}
            label="number"
            onChange={handleAddressNumberChangeWrapper}
            onBlur={handleAddressNumberBlurWrapper}
            loading={zipState?.updatingCoordinates}
          />
        </Col>
      </Row>

      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={showNeighborhood ? 8 : 12}>
          <SimpleInput name={address2ElementProps.fullFieldName} label="fraction" />
        </Col>

        {showNeighborhood ? (
          <Col xs={24} sm={8}>
            <SimpleInput
              name={neighborhoodElementProps.fullFieldName}
              label="locality"
              disabled={!zipState?.zipWithMultipleAddress && !zipState?.apiFailure}
            />
          </Col>
        ) : null}

        {/* Concelho + Distrito as distinct PT fields (no BR-style "city - UF" combo) */}
        <Col xs={24} sm={showNeighborhood ? 8 : 12}>
          <Row gutter={ROW_GUTTER}>
            <Col xs={24} sm={14}>
              <SimpleInput
                name={cityElementProps.fullFieldName}
                label="municipality"
                disabled={!zipState?.zipWithMultipleAddress && !zipState?.apiFailure}
              />
            </Col>
            <Col xs={24} sm={10}>
              <SimpleInput
                name={regionElementProps.fullFieldName}
                label="district"
                disabled={!zipState?.zipWithMultipleAddress && !zipState?.apiFailure}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {showAddressReference ? (
        <Row gutter={ROW_GUTTER}>
          <Col xs={24}>
            <SimpleInput
              name={addressReferenceElementProps.fullFieldName}
              label="reference_point"
            />
          </Col>
        </Row>
      ) : null}

      {showMap ? (
        <Row gutter={ROW_GUTTER}>
          <Col xs={24}>
            <SimpleGoogleMap subdocumentField={subdocumentField} />
          </Col>
        </Row>
      ) : null}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleAddressFormPT;

// *******************************************************************************
// HELPERS
// *******************************************************************************
